import React, { useState, useEffect } from 'react';
import { Checkbox, Button, Modal, Typography, Space, notification, Form, Spin } from 'antd';
import Navbar from './Navbar';
import './DataPrivacy.css';

const { Title, Paragraph } = Typography;

const DataPrivacy = () => {
  const [form] = Form.useForm();
  const [privacySettings, setPrivacySettings] = useState({
    fullyPrivate: false,
    selectedProviders: false,
    generalProviders: false,
    research: false,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchPrivacySettings = async () => {
      try {
        const response = await fetch('/backend/save_privacy_settings.php', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        const data = await response.json();
        if (response.ok && !data.error) {
          setPrivacySettings({
            fullyPrivate: !!data.fullyPrivate,
            selectedProviders: !!data.selectedProviders,
            generalProviders: !!data.generalProviders,
            research: !!data.research,
          });
          form.setFieldsValue({
            privacyOptions: data,
          });
        } else {
          notification.error({
            message: 'Erreur',
            description: data.error || 'Erreur lors de la récupération des préférences.',
            placement: 'topRight',
            duration: 3,
          });
        }
      } catch (error) {
        notification.error({
          message: 'Erreur',
          description: 'Une erreur s\'est produite lors de la récupération des préférences.',
          placement: 'topRight',
          duration: 3,
        });
      }
    };

    fetchPrivacySettings();
  }, [form]);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setPrivacySettings((prev) => ({
      ...prev,
      [name]: checked,
      ...(name === 'fullyPrivate' && checked
        ? { selectedProviders: false, generalProviders: false, research: false }
        : {}),
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await form.validateFields();
      const response = await fetch('/backend/save_privacy_settings.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(privacySettings),
      });

      const data = await response.json();
      if (response.ok) {
        notification.success({
          message: 'Settings Saved',
          description: 'Your privacy settings have been successfully saved.',
          placement: 'topRight',
          duration: 3,
        });
      } else {
        notification.error({
          message: 'Erreur',
          description: data.error || 'Erreur lors de la sauvegarde des préférences.',
          placement: 'topRight',
          duration: 3,
        });
      }
    } catch (error) {
      notification.error({
        message: 'Erreur',
        description: 'Une erreur s\'est produite lors de la sauvegarde des préférences.',
        placement: 'topRight',
        duration: 3,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setPrivacySettings({
      fullyPrivate: false,
      selectedProviders: false,
      generalProviders: false,
      research: false,
    });
    form.resetFields();
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  return (
    <div className="data-privacy-wrapper">
      <Navbar />
      <div className="data-privacy-container">
        <Title level={2} className="data-privacy-title">Data Privacy and Sharing</Title>
        <Paragraph className="data-privacy-description">
          Manage your data privacy and sharing preferences with the options below:
        </Paragraph>
        <Form
          form={form}
          layout="vertical"
          initialValues={privacySettings}
          className="data-privacy-form"
        >
          <Form.Item
            name="privacyOptions"
            rules={[
              {
                validator: (_, value) =>
                  privacySettings.fullyPrivate ||
                  privacySettings.selectedProviders ||
                  privacySettings.generalProviders ||
                  privacySettings.research
                    ? Promise.resolve()
                    : Promise.reject('Please select at least one privacy option.'),
              },
            ]}
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Checkbox
                name="fullyPrivate"
                checked={privacySettings.fullyPrivate}
                onChange={handleCheckboxChange}
                aria-label="Keep my data fully private"
                className="data-privacy-checkbox"
              >
                My User details and data remain fully private and not shared (information will not be shared with service providers or third parties).
              </Checkbox>
              <Checkbox
                name="selectedProviders"
                checked={privacySettings.selectedProviders}
                onChange={handleCheckboxChange}
                disabled={privacySettings.fullyPrivate}
                aria-label="Share with selected providers"
                className="data-privacy-checkbox"
              >
                Share my User details and data with parties and service providers that I select.
              </Checkbox>
              <Checkbox
                name="generalProviders"
                checked={privacySettings.generalProviders}
                onChange={handleCheckboxChange}
                disabled={privacySettings.fullyPrivate}
                aria-label="Share with general providers"
                className="data-privacy-checkbox"
              >
                Share my User details and data with service providers in the homelessness response system to support coordination of services for resources, services, and housing.
              </Checkbox>
              <Checkbox
                name="research"
                checked={privacySettings.research}
                onChange={handleCheckboxChange}
                disabled={privacySettings.fullyPrivate}
                aria-label="Share for research purposes"
                className="data-privacy-checkbox"
              >
                Share my User details and data for reporting, research, and/or analysis about the housing crisis and homelessness response system.
              </Checkbox>
            </Space>
          </Form.Item>
          <Space size="middle" className="data-privacy-actions">
            <Button
              type="primary"
              onClick={handleSave}
              aria-label="Save privacy settings"
              className="data-privacy-button"
              disabled={isSaving}
            >
              {isSaving ? <Spin size="small" /> : 'Save Settings'}
            </Button>
            <Button
              onClick={handleReset}
              aria-label="Reset privacy settings"
              className="data-privacy-button"
              disabled={isSaving}
            >
              Reset
            </Button>
            <Button
              onClick={showModal}
              aria-label="View privacy policy"
              className="data-privacy-button"
              disabled={isSaving}
            >
              View Privacy Policy
            </Button>
          </Space>
        </Form>

        <Modal
          title={<span className="modal-title">NH GUIDE Data Sharing Policy</span>}
          open={isModalVisible}
          onCancel={handleModalClose}
          footer={[
            <Button
              key="close"
              onClick={handleModalClose}
              aria-label="Close privacy policy modal"
              className="data-privacy-button"
            >
              Close
            </Button>,
          ]}
          width={900}
          className="data-privacy-modal"
        >
          <div className="modal-content-scrollable">
            <Title level={4}>NH GUIDE Data Sharing and Release of Information Policy</Title>
            <Paragraph>
              The NH GUIDE allows you (“user”) to share your information, including your protected personally identifiable information (“PII”), with third-party organizations, participating service providers, and the housing crisis response system.
            </Paragraph>
            <Title level={5}>Purposes for Sharing Information</Title>
            <Paragraph>
              User information shared in the NH GUIDE helps:
              <ul>
                <li>Coordinate and connect you with housing programs, services, or resources, including shelter and housing solutions.</li>
                <li>Support research about housing, homelessness services, and the housing crisis response system.</li>
                <li>Improve and advance the provision of services and the housing crisis response system.</li>
              </ul>
            </Paragraph>
            <Title level={5}>Parties for Information Sharing</Title>
            <Paragraph>
              Services and support are provided by organizations, service providers, and agencies in the housing crisis response system, including homelessness prevention, shelter and housing solutions, medical, mental, and behavioral health, and related software services.
            </Paragraph>
            <Paragraph>
              Any user information shared complies with Federal, State, local, or municipal government regulations, legal requirements, or court orders, and aims to preserve personal and/or public safety.
            </Paragraph>
            <Title level={5}>Types of Information Shared</Title>
            <Paragraph>
              Information shared in the NH GUIDE may include details about programs and services you receive or have received, such as:
              <ul>
                <li>Legal First and Last Name</li>
                <li>Other Chosen Names</li>
                <li>Location and/or Home Address</li>
                <li>Personal Email Address</li>
                <li>Social Security Number</li>
                <li>Identification Details (Passport Number, Driver’s License Number)</li>
                <li>Date of Birth</li>
                <li>Telephone Number</li>
                <li>Medical Information</li>
                <li>Criminal Justice System History/Details</li>
                <li>Photos/Images</li>
              </ul>
            </Paragraph>
            <Title level={5}>Opting Not to Share Information</Title>
            <Paragraph>
              If you choose not to share your information through NH GUIDE, your data will not be shared for marketing purposes.
            </Paragraph>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default DataPrivacy;